// src/pages/Services.js - COMPLETE VERSION WITH CMS CONTENT
import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import './Services.css';

// 🔥 API URL (trimmed, no duplicate path)
const WP_API_URL = 'https://cms.vxpath.com/wp-json/wp/v2';

// Plain helper function (NOT a hook - can be called anywhere)
const parseFeatures = (raw) => {
  if (!raw || typeof raw !== 'string') return [];
  return raw.split(/\r?\n/).map(line => line.trim()).filter(line => line);
};

// ============================================================================
// TAB_CONFIG - MOVED OUTSIDE COMPONENT TO PREVENT RECREATION ON EVERY RENDER
// ============================================================================
const TAB_CONFIG = [
  { id: 'network', label: 'Network', icon: '🌐', description: 'Enterprise networking solutions including routing, switching, and infrastructure design'},
  { id: 'net-security', label: 'Net-Security', icon: '🛡️', description: 'Network security services including Cisco ISE, firewalls, IDS/IPS, and threat protection'},
  { id: 'wireless', label: 'Wireless', icon: '📡', description: 'Wireless networking solutions including WiFi, mobility, and location services'},
  { id: 'cloud-network', label: 'Cloud-Network', icon: '☁️', description: 'Hybrid cloud connectivity, multi-cloud networking, and cloud-native infrastructure'},
  { id: 'ai-automation', label: 'Ai & Automation', icon: '🧬', description: 'Network automation and orchestration using Agentic AI, Nornir, Python, Ansible, and other automation tools'},
  { id: 'net-technician', label: 'Network Technician', icon: '🔧', description: 'On-site technical support,fixing cabling, installation, and maintenance services'},
  { id: 'surveillance', label: 'Surveillance', icon: '📹', description: 'IP camera systems, video surveillance, and security monitoring solutions'},
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const Services = () => {
  // 🔥 Route params for detail view
  const { slug } = useParams();
  const location = useLocation();
  const isDetailView = !!slug;

  // State for list view
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('network');
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const tabRefs = useRef({});

  // State for detail view
  const [detailService, setDetailService] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  // ============================================================================
  // 🔥 FIX: Define activeCategory/activeTabConfig EARLY (before any returns)
  // ============================================================================
  const activeCategory = categories.find(cat => cat.id === activeTab);
  const activeTabConfig = TAB_CONFIG.find(tab => tab.id === activeTab);

  // ============================================================================
  // DEFINE ALL FUNCTIONS BEFORE JSX RETURN
  // ============================================================================

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (window.location.hash !== `#${tabId}`) {
      window.history.pushState(null, null, `#${tabId}`);
    }
  };

  const getCategoryDescription = (name) => {
    const descriptions = {
      'Network': 'Enterprise networking solutions including routing, switching, and infrastructure design',
      'Net-Security': 'Network security services including firewalls, IDS/IPS, and threat protection',
      'Ai-Automation': 'Network automation and orchestration using Agentic AI, Nornir, Python, Ansible, and other automation tools',
      'Wireless': 'Wireless networking solutions including WiFi, mobility, and location services',
      'Surveillance': 'IP camera systems, video surveillance, and security monitoring solutions',
      'Net-Tech Support': 'On-site technical support, installation, and maintenance services',
    };
    return descriptions[name] || 'Professional IT services tailored to your needs.';
  };

  // ============================================================================
  // FETCH SERVICES LIST (only if in list view)
  // ============================================================================
  useEffect(() => {
    if (isDetailView) return;

    const fetchServices = async () => {
      try {
        const response = await fetch(`${WP_API_URL}/service?_embed&per_page=100`);
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

        const services = await response.json();
        const filteredServices = services.filter(service => service.slug !== 'qa');

        const iconMap = {
          'Network': '🌐', 'Net-Security': '🛡️', 'Wireless': '📡', 'Cloud-Network': '☁️','Ai & Automation': '🤖',
          'Net-Tech Support': '🔧', 'Surveillance': '📹', 
        };

        const grouped = {};

        filteredServices.forEach((service) => {
          const rawTitle = service.title.rendered;
          const cleanTitle = rawTitle.replace(/&amp;/g, '&');

          let categoryId = 'network';
          let categoryName = 'Network';
          const termNames = service.service_category_names || [];
          const validTerm = termNames.find(term => term && term.trim() !== '' && term.toLowerCase() !== 'service');

          if (validTerm) {
            const termLower = validTerm.toLowerCase();
            if (termLower.includes('automation') || termLower.includes('ai')) {
              categoryId = 'ai-automation';
              categoryName = 'Ai & Automation';
            } else if (termLower.includes('security')) {
              categoryId = 'net-security';
              categoryName = 'Net-Security';
            } else if (termLower.includes('wireless') || termLower.includes('wifi')) {
              categoryId = 'wireless';
              categoryName = 'Wireless';
            } else if (termLower.includes('surveillance') || termLower.includes('camera')) {
              categoryId = 'surveillance';
              categoryName = 'Surveillance';
            } else if (termLower.includes('technician') || termLower.includes('support')) {
              categoryId = 'net-technician';
              categoryName = 'Net-Tech Support';
            } else if (termLower.includes('cloud')) { // ✅ ADD THIS
              categoryId = 'cloud-network';
              categoryName = 'Cloud-Network';
            } else if (termLower.includes('network')) {
              categoryId = 'network';
              categoryName = 'Network';
            }
          } else {
            const titleLower = cleanTitle.toLowerCase();
            if (titleLower.includes('automation') || titleLower.includes('ai')) {
              categoryId = 'ai-automation';
              categoryName = 'Ai & Automation';
            } else if (titleLower.includes('security') || titleLower.includes('firewall')) {
              categoryId = 'net-security';
              categoryName = 'Net-Security';
            } else if (titleLower.includes('wireless') || titleLower.includes('wifi')) {
              categoryId = 'wireless';
              categoryName = 'Wireless';
            } else if (titleLower.includes('surveillance') || titleLower.includes('camera')) {
              categoryId = 'surveillance';
              categoryName = 'Surveillance';
            } else if (titleLower.includes('technician') || titleLower.includes('support')) {
              categoryId = 'net-technician';
              categoryName = 'Net-Tech Support';
            } else if (titleLower.includes('cloud')) { // ✅ ADD THIS
              categoryId = 'cloud-network';
              categoryName = 'Cloud-Network';
            }
          }

          const validTabs = ['network', 'net-security', 'ai-automation', 'wireless', 'surveillance', 'net-technician','cloud-network',];
          if (!validTabs.includes(categoryId)) return;

          if (!grouped[categoryId]) {
            grouped[categoryId] = {
              id: categoryId, title: categoryName, icon: iconMap[categoryName] || '🌐',
              description: getCategoryDescription(categoryName), services: [],
            };
          }

          const acf = service.acf || {};
          const features = parseFeatures(acf?.features_list);
          const featuredImage = service._embedded?.['wp:featuredmedia']?.[0]?.source_url || null;

          grouped[categoryId].services.push({
            title: cleanTitle,
            desc: acf.short_description || service.excerpt?.rendered || '',
            features,
            slug: service.slug,
            id: service.id,
            image: featuredImage,
            fullDescription: acf.full_description || '',
            technicalSpecs: acf.technical_specs || '',
            benefits: acf.benefits || '',
          });
        });

        setCategories(Object.values(grouped));
      } catch (err) {
        console.error('Error fetching services:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [isDetailView]);

  // ============================================================================
  // FETCH SERVICE DETAIL (only if in detail view)
  // ============================================================================
  useEffect(() => {
    if (!isDetailView || !slug) return;

    const fetchDetail = async () => {
      const passedService = location.state?.service;
      if (passedService) {
        setDetailService(passedService);
        return;
      }

      try {
        setDetailLoading(true);
        setDetailError(null);

        const response = await fetch(
          `${WP_API_URL}/service?slug[]=${encodeURIComponent(slug)}&_embed`
        );

        // ============================================================================
        // 🔥 FIX: Declare serviceData once to avoid redeclaration error
        // ============================================================================
        let serviceData;

        if (!response.ok) {
          const searchResp = await fetch(
            `${WP_API_URL}/service?search=${encodeURIComponent(slug)}&_embed&per_page=1`
          );
          if (!searchResp.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          const searchData = await searchResp.json();
          if (!searchData?.length) throw new Error(`Service "${slug}" not found`);
          serviceData = searchData[0];
        } else {
          const data = await response.json();
          if (!data?.length) throw new Error(`Service "${slug}" not found`);
          serviceData = data[0];
        }

        const acf = serviceData.acf || {};
        const features = parseFeatures(acf?.features_list);

        setDetailService({
          id: serviceData.id,
          title: serviceData.title?.rendered?.replace(/&amp;/g, '&') || 'Untitled',
          slug: serviceData.slug,
          desc: acf?.short_description || serviceData.excerpt?.rendered || '',
          fullDescription: acf?.full_description || '',
          features,
          technicalSpecs: acf?.technical_specs || '',
          benefits: acf?.benefits || '',
          image: serviceData._embedded?.['wp:featuredmedia']?.[0]?.source_url || null,
        });

      } catch (err) {
        console.error('Error fetching service detail:', err);
        setDetailError(err.message);
      } finally {
        setDetailLoading(false);
      }
    };

    fetchDetail();
  }, [isDetailView, slug, location.state?.service]);

  // ============================================================================
  // INDICATOR ANIMATION (list view only)
  // ============================================================================
  useEffect(() => {
    if (isDetailView) return;
    const updateIndicator = () => {
      const activeButton = tabRefs.current[activeTab];
      if (activeButton) {
        const { offsetLeft, offsetWidth } = activeButton;
        setIndicatorStyle({ left: `${offsetLeft}px`, width: `${offsetWidth}px` });
      }
    };
    const timer = setTimeout(updateIndicator, 50);
    window.addEventListener('resize', updateIndicator);
    return () => { clearTimeout(timer); window.removeEventListener('resize', updateIndicator); };
  }, [activeTab, categories, isDetailView]);

  // ============================================================================
  // HASH-BASED TAB ACTIVATION (list view only)
  // ============================================================================
  useEffect(() => {
    if (isDetailView) return;
    const handleHash = () => {
      const hash = window.location.hash.replace('#', '').trim();
      if (hash && TAB_CONFIG.some(tab => tab.id === hash)) {
        setActiveTab(hash);
        setTimeout(() => {
          const element = document.getElementById('tabs-content');
          if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      }
    };
    if (!loading && categories.length > 0) handleHash();
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, [loading, categories, isDetailView]);

  // ============================================================================
  // EARLY RETURNS: Loading & Error States
  // ============================================================================
  
  // Detail view loading
  if (isDetailView && detailLoading) {
    return (
      <div className="service-detail-page">
        <div className="container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading service details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Detail view error
  if (isDetailView && (detailError || !detailService)) {
    return (
      <div className="service-detail-page">
        <div className="container">
          <div className="error-state">
            <h2>Service Not Found</h2>
            <p>{detailError || 'The service you\'re looking for doesn\'t exist.'}</p>
            <Link to="/services" className="btn-primary">Back to Services</Link>
          </div>
        </div>
      </div>
    );
  }

  // List view loading
  if (!isDetailView && loading) {
    return (
      <div className="services-page">
        <div className="container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading services...</p>
          </div>
        </div>
      </div>
    );
  }

  // List view error
  if (!isDetailView && error) {
    return (
      <div className="services-page">
        <div className="container">
          <div className="error-state">
            <p className="error">Failed to load services: {error}</p>
            <p>Please check the WordPress admin or try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // DETAIL VIEW: Show Actual CMS Content (ACF Fields from WordPress)
  // ============================================================================
  if (isDetailView && detailService) {
    return (
      <div className="service-detail-page">
        {/* Breadcrumb Navigation */}
        <nav className="breadcrumb-nav" aria-label="Breadcrumb">
          <div className="container">
            <ol className="breadcrumb-list">
              <li className="breadcrumb-item">
                <Link to="/" className="breadcrumb-link">🏠 Home</Link>
              </li>
              <li className="breadcrumb-separator">/</li>
              <li className="breadcrumb-item">
                <Link to="/services" className="breadcrumb-link">Services</Link>
              </li>
              <li className="breadcrumb-separator">/</li>
              <li className="breadcrumb-item active" aria-current="page">
                {detailService.title}
              </li>
            </ol>
          </div>
        </nav>

        {/* Hero Section */}
        <header className="service-hero-section">
          <div className="container">
            <div className="service-hero-grid">
              <div className="service-hero-content">
                <h1 className="service-title">{detailService.title}</h1>
                {/* <p className="service-excerpt">{detailService.desc}</p> */}
                <div className="service-hero-actions">
                  <Link to="/contact" className="btn-primary btn-large">
                    🚀 Get Started
                  </Link>
                  <button 
                    onClick={() => window.history.back()} 
                    className="btn-secondary btn-large"
                  >
                    ← Back
                  </button>
                </div>
              </div>
              {/* {detailService.image && (
                <div className="service-hero-image-wrapper">
                  <div className="image-container">
                    <img 
                      src={detailService.image} 
                      alt={detailService.title} 
                      className="service-hero-image" 
                      loading="eager" 
                    />
                  </div>
                </div>
              )} */}
            </div>
          </div>
        </header>

        {/* Main Content - ACTUAL CMS CONTENT */}
        <main className="service-main-content">
          <div className="container">
            <div className="content-grid">
              <article className="service-article">
                
                {/* 🔥 OVERVIEW: Display actual CMS full_description */}
                {detailService.fullDescription && (
                  <section className="content-section overview-section">
                    <div className="section-header">
                      <h2 className="section-title">Overview</h2>
                      <div className="section-divider"></div>
                    </div>
                    <div 
                      className="cms-content"
                      dangerouslySetInnerHTML={{ __html: detailService.fullDescription }}
                    />
                  </section>
                )}

                {/* 🔥 KEY FEATURES: Display actual CMS features_list */}
                {detailService.features?.length > 0 && (
                  <section className="content-section features-section">
                    <div className="section-header">
                      <h2 className="section-title">Key Features</h2>
                      <div className="section-divider"></div>
                    </div>
                    <ul className="cms-features-list">
                      {detailService.features.map((feature, idx) => (
                        <li key={idx} className="cms-feature-item">
                          <span className="feature-check">✓</span>
                          <span className="feature-text">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* 🔥 BENEFITS: Display actual CMS benefits field */}
                {detailService.benefits && (
                  <section className="content-section benefits-section">
                    <div className="section-header">
                      <h2 className="section-title">Benefits</h2>
                      <div className="section-divider"></div>
                    </div>
                    <div 
                      className="cms-content"
                      dangerouslySetInnerHTML={{ __html: detailService.benefits }}
                    />
                  </section>
                )}

                {/* 🔥 TECHNICAL SPECS: Display actual CMS technical_specs field */}
                {detailService.technicalSpecs && (
                  <section className="content-section specs-section">
                    <div className="section-header">
                      <h2 className="section-title">Technical Specifications</h2>
                      <div className="section-divider"></div>
                    </div>
                    <div 
                      className="cms-content"
                      dangerouslySetInnerHTML={{ __html: detailService.technicalSpecs }}
                    />
                  </section>
                )}

                {/* Fallback if no content fields are populated */}
                {!detailService.fullDescription && 
                 !detailService.benefits && 
                 !detailService.technicalSpecs && 
                 detailService.features?.length === 0 && (
                  <section className="content-section">
                    <p className="no-content-message">
                      <em>No detailed content available for this service yet. 
                      Please check back soon or contact us for more information.</em>
                    </p>
                  </section>
                )}
              </article>

              {/* Sidebar */}
              <aside className="service-sidebar">
                <div className="sidebar-card cta-card">
                  <h3 className="card-title">Ready to Get Started?</h3>
                  <p className="card-subtitle">Contact our team for a free consultation</p>
                  <Link to="/contact" className="btn-primary btn-block">
                    Contact Us
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </main>

        {/* CTA Section */}
        <section className="service-cta-section">
          <div className="container">
            <div className="cta-content">
              <h2 className="cta-title">Ready to Transform Your Infrastructure?</h2>
              <p className="cta-description">
                Our team is ready to help you implement <strong>{detailService.title}</strong>.
              </p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn-primary">Contact Sales</Link>
                <Link to="/services" className="btn-secondary">View All Services</Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ============================================================================
  // LIST VIEW: Render Services Grid
  // ============================================================================
  return (
    <div className="services-page">
      <header className="page-header">
        <h1>Enterprise IT Solutions</h1>
        <p>Comprehensive networking and infrastructure services for modern businesses</p>
      </header>

      {/* Tab Navigation */}
      <div className="container">
        <div className="tabs-wrapper">
          <div className="tabs-nav" role="tablist">
            <div className="tab-indicator" style={indicatorStyle} aria-hidden="true" />
            {TAB_CONFIG.map((tab) => {
              const category = categories.find(c => c.id === tab.id);
              const count = category?.services?.length || 0;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  ref={el => tabRefs.current[tab.id] = el}
                  className={`tab-button ${isActive ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                  aria-selected={isActive}
                  role="tab"
                  aria-controls={`panel-${tab.id}`}
                  title={tab.description}
                >
                  <span className="tab-icon" aria-hidden="true">{tab.icon}</span>
                  <span className="tab-content">
                    <span className="tab-label">{tab.label}</span>
                  </span>
                  {count > 0 && <span className="tab-count">{count}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="container" id="tabs-content">
        {activeCategory ? (
          <section className="service-category tab-panel active" role="tabpanel" id={`panel-${activeTab}`} aria-labelledby={`tab-${activeTab}`}>
            <div className="category-header">
              <div className="category-icon">{activeCategory.icon}</div>
              <div className="category-info">
                <h2>{activeTabConfig?.label || 'Services'}</h2>
                <p className="category-description">{activeTabConfig?.description || activeCategory.description}</p>
              </div>
            </div>
            <div className="service-grid">
              {activeCategory.services.map((service, idx) => (
                <div className="service-card" key={service.id || idx}>
                  {service.image && (
                    <div className="service-image">
                      <img src={service.image} alt={service.title} loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                  )}
                  <div className="service-content">
                    <h3>{service.title}</h3>
                    <p className="service-desc">{service.desc}</p>
                    {service.features?.length > 0 && (
                      <ul className="service-features">
                        {service.features.slice(0, 4).map((feature, i) => <li key={i}>{feature}</li>)}
                      </ul>
                    )}
                    <Link to={`/service/${service.slug}`} className="service-cta" state={{ service }}>
                      Learn More →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : (
          <div className="no-services">
            <p>No services available in this category yet.</p>
            <p className="no-services-hint">Check back soon or contact us for more information.</p>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="services-cta">
        <div className="container">
          <h2>Ready to Transform Your Infrastructure?</h2>
          <p>Schedule a free consultation with our solutions architects.</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn-primary">Get Started</Link>
            <Link to="/contact?type=consultation" className="btn-secondary">Book Consultation</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// 🔥 CRITICAL: Export default at the VERY END
export default Services;